import { limit, orderBy, query } from 'firebase/firestore';
import { useMemo } from 'react';
import { useCollection, useDoc } from '@/lib/firestore/hooks';
import { assignmentRulesCol, caseDoc, casesCol } from '@/lib/firestore/refs';
import type { AssignmentRule, Case, CaseState } from '@shared/types';

const ACTIVE = new Set<CaseState>(['New', 'Open', 'Awaiting Info']);

/** Recent cases ordered by update; state and company filters are client-side (no composite index needed). */
export function useCases(state: 'All' | 'Active' | CaseState) {
  const { data, loading } = useCollection<Case>(useMemo(() => query(casesCol(), orderBy('updatedAt', 'desc'), limit(500)), []), []);
  const filtered = useMemo(() => {
    if (state === 'All') return data;
    if (state === 'Active') return data.filter((c) => ACTIVE.has(c.state));
    return data.filter((c) => c.state === state);
  }, [data, state]);
  return { data: filtered, loading };
}
export const useCase = (id: string | null) => useDoc<Case>(id ? caseDoc(id) : null);
export function useCompanyCases(companyId: string | null) {
  const { data, loading } = useCollection<Case>(useMemo(() => query(casesCol(), orderBy('updatedAt', 'desc'), limit(500)), []), []);
  const filtered = useMemo(() => (companyId ? data.filter((c) => c.companyId === companyId) : []), [data, companyId]);
  return { data: filtered, loading };
}
export const useAssignmentRules = () => useCollection<AssignmentRule>(useMemo(() => query(assignmentRulesCol(), orderBy('order')), []), []);
